import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { useAuth } from '../contexts/AuthContext';
import * as opportunitiesApi from '../api/opportunities';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import Modal from '../components/Modal';
import MessageModal from '../components/MessageModal';

// Utility functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return 'Not set';
  try {
    // Handle both old format (with timezone) and new format (DATE only)
    if (dateString.includes('T') && dateString.includes('Z')) {
      // Legacy format: extract date part and create as local date
      const dateOnly = dateString.split('T')[0];
      return new Date(dateOnly + 'T00:00:00').toLocaleDateString();
    } else {
      // New format: simple DATE string (YYYY-MM-DD)
      return new Date(dateString + 'T00:00:00').toLocaleDateString();
    }
  } catch (error) {
    return 'Invalid date';
  }
};

const formatTime = (timeString) => {
  if (!timeString) return '';
  try {
    // Handle formats like "15:00:00" or "15:00"
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const minute = parseInt(minutes, 10);

    // Convert to 12-hour format
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const displayMinute = minute === 0 ? '' : `:${minutes}`;

    return `${displayHour}${displayMinute} ${ampm}`;
  } catch (error) {
    return timeString; // Return original if parsing fails
  }
};

function SalesPipelinePage() {
  const { loading: authLoading, user } = useAuth();
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [showNewOpportunityModal, setShowNewOpportunityModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [showEditAppointmentModal, setShowEditAppointmentModal] = useState(false);
  const [showEditOpportunityModal, setShowEditOpportunityModal] = useState(false);
  const [openSections, setOpenSections] = useState({}); // For mobile accordion
  const [clients, setClients] = useState([]);

  const stages = [
    { key: 'lead', title: 'New Leads', icon: '👥' },
    { key: 'appointment_requested', title: 'Appointment Requested', icon: '📞' },
    { key: 'appointment_scheduled', title: 'Appointment Scheduled', icon: '📅' },
    { key: 'quote_draft', title: 'Draft Quote', icon: '📝' },
    { key: 'quote_sent', title: 'Quote Sent', icon: '📤' },
    { key: 'quote_accepted', title: 'Won', icon: '✅' },
    { key: 'in_progress', title: 'In Progress', icon: '🔨' },
    { key: 'lost', title: 'Lost', icon: '❌' }
  ];

  // Stage transition logic
  const stageTransitions = {
    'lead': ['appointment_requested', 'appointment_scheduled', 'quote_draft', 'lost'],
    'appointment_requested': ['appointment_scheduled', 'lost'],
    'appointment_scheduled': ['quote_draft', 'lost'],
    'quote_draft': ['quote_sent', 'on_hold', 'lost'],
    'quote_sent': ['quote_accepted', 'on_hold', 'lost'],
    'on_hold': ['quote_sent', 'quote_accepted', 'lost'],
    'quote_accepted': ['in_progress', 'completed'], // Won - move to work
    'in_progress': ['completed'],
    'lost': ['lead'], // Can re-engage
    'completed': [] // Final state
  };

  const stageLabels = {
    'lead': 'New Lead',
    'appointment_requested': 'Appointment Requested',
    'appointment_scheduled': 'Appointment Scheduled',
    'quote_draft': 'Draft Quote',
    'quote_sent': 'Quote Sent',
    'quote_accepted': 'Won',
    'in_progress': 'In Progress',
    'completed': 'Completed',
    'lost': 'Lost',
    'on_hold': 'On Hold'
  };

  useEffect(() => {
    if (!authLoading) {
      fetchPipelineData();
    }
  }, [authLoading]);

  const fetchPipelineData = async () => {
    try {
      setError(null);
      setLoading(true);

      // Fetch all opportunities
      const response = await opportunitiesApi.getOpportunities();
      const opportunities = response.opportunities || [];

      // Group opportunities by status
      const groupedPipeline = {};
      stages.forEach(stage => {
        groupedPipeline[stage.key] = [];
      });

      opportunities.forEach(opp => {
        if (groupedPipeline[opp.status]) {
          groupedPipeline[opp.status].push(opp);
        }
      });

      setPipeline(groupedPipeline);
    } catch (err) {
      console.error('Error fetching pipeline:', err);
      setError(err.message || 'Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (opportunityId, newStatus) => {
    try {
      // Update opportunity status via API
      await opportunitiesApi.updateOpportunityStatus(opportunityId, newStatus);

      // Update local state
      const newPipeline = { ...pipeline };
      let movedOpportunity = null;

      // Find and remove the opportunity from its current stage
      Object.keys(newPipeline).forEach(status => {
        const oppIndex = newPipeline[status].findIndex(opp => opp.id === opportunityId);
        if (oppIndex > -1) {
          movedOpportunity = newPipeline[status][oppIndex];
          newPipeline[status].splice(oppIndex, 1);
        }
      });

      // Add to new stage
      if (movedOpportunity) {
        if (!newPipeline[newStatus]) newPipeline[newStatus] = [];
        movedOpportunity.status = newStatus;
        newPipeline[newStatus].push(movedOpportunity);
      }

      setPipeline(newPipeline);
      setSuccess('Opportunity moved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error moving opportunity:', err);
      setError(err.message || 'Failed to move opportunity');
    }
  };

  const handleCreateQuote = async (opportunity) => {
    try {
      setLoading(true);
      setError(null);

      // If opportunity already has a quote, redirect to edit that quote
      if (opportunity.quote_id) {
        window.location.href = `/quotes/edit/${opportunity.quote_id}`;
        return;
      }

      // Import quotes API to create the quote immediately
      const { createQuote } = await import('../api/quotes');

      // Create a minimal draft quote with opportunity data
      const quoteData = {
        clientId: opportunity.client_id,
        opportunityId: opportunity.id,
        quoteName: opportunity.project_name ? `${opportunity.project_name} Quote` : `Quote for ${opportunity.client_name}`,
        overallTaxRate: 0.08, // 8% default tax rate
        discountType: 'percentage',
        discountValue: 0,
        expirationDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
        quoteTermsConditions: '', // Will be filled with user defaults in the form
        sections: [], // Empty sections - user will add these
        status: 'draft'
      };

      // Create the quote
      const response = await createQuote(quoteData);
      const newQuoteId = response.quote.id;

      // Link the quote to the opportunity and update status
      await opportunitiesApi.linkQuoteToOpportunity(opportunity.id, newQuoteId);
      await opportunitiesApi.updateOpportunityStatus(opportunity.id, 'quote_draft');

      // Redirect to edit mode for the newly created quote with opportunity context
      window.location.href = `/quotes/edit/${newQuoteId}?opportunityId=${opportunity.id}`;

    } catch (err) {
      console.error('Error creating quote from opportunity:', err);
      setError(err.message || 'Failed to create quote');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleAppointment = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowScheduleModal(true);
  };

  const handleEditAppointment = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowEditAppointmentModal(true);
  };

  const handleEditOpportunity = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowEditOpportunityModal(true);
  };

  const toggleMobileSection = (stageKey) => {
    setOpenSections(prev => ({
      ...prev,
      [stageKey]: !prev[stageKey]
    }));
  };

  if (authLoading || loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header with Quick Actions */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h1 className="text-3xl font-bold text-primary-600">Sales Pipeline</h1>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <button
                onClick={() => setShowNewAppointmentModal(true)}
                className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition duration-300"
              >
                + New Appointment
              </button>
              <button
                onClick={() => setShowNewOpportunityModal(true)}
                className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition duration-300"
              >
                + New Opportunity
              </button>
              <Link
                to="/quotes/new"
                className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition duration-300 text-center"
              >
                + New Quote
              </Link>
            </div>
          </div>
        </div>

        {success && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md">
            {success}
          </div>
        )}

        {/* Desktop Grid Layout (4x2) */}
        <div className="hidden lg:grid lg:grid-cols-4 lg:gap-6 mb-8">
          {stages.map((stage) => (
            <StageBox
              key={stage.key}
              stage={stage}
              opportunities={pipeline[stage.key] || []}
              onStageChange={handleStageChange}
              onCreateQuote={handleCreateQuote}
              onScheduleAppointment={handleScheduleAppointment}
              onEditAppointment={handleEditAppointment}
              onEditOpportunity={handleEditOpportunity}
              stageTransitions={stageTransitions}
              stageLabels={stageLabels}
            />
          ))}
        </div>

        {/* Mobile Accordion Layout */}
        <div className="lg:hidden space-y-4">
          {stages.map((stage) => (
            <MobileStageSection
              key={stage.key}
              stage={stage}
              opportunities={pipeline[stage.key] || []}
              isOpen={openSections[stage.key]}
              onToggle={() => toggleMobileSection(stage.key)}
              onStageChange={handleStageChange}
              onCreateQuote={handleCreateQuote}
              onScheduleAppointment={handleScheduleAppointment}
              onEditAppointment={handleEditAppointment}
              onEditOpportunity={handleEditOpportunity}
              stageTransitions={stageTransitions}
              stageLabels={stageLabels}
            />
          ))}
        </div>

        {/* New Opportunity Modal */}
        {showNewOpportunityModal && (
          <NewOpportunityModal
            onClose={() => setShowNewOpportunityModal(false)}
            onSuccess={() => {
              setShowNewOpportunityModal(false);
              fetchPipelineData();
            }}
          />
        )}

        {/* Schedule Appointment Modal */}
        {showScheduleModal && selectedOpportunity && (
          <ScheduleAppointmentModal
            opportunity={selectedOpportunity}
            onClose={() => {
              setShowScheduleModal(false);
              setSelectedOpportunity(null);
            }}
            onSuccess={() => {
              setShowScheduleModal(false);
              setSelectedOpportunity(null);
              fetchPipelineData();
              setSuccess('Appointment scheduled successfully!');
              setTimeout(() => setSuccess(null), 3000);
            }}
            user={user}
          />
        )}

        {/* New Appointment Modal (without opportunity) */}
        {showNewAppointmentModal && (
          <NewAppointmentModal
            onClose={() => setShowNewAppointmentModal(false)}
            onSuccess={() => {
              setShowNewAppointmentModal(false);
              fetchPipelineData();
              setSuccess('Appointment scheduled successfully!');
              setTimeout(() => setSuccess(null), 3000);
            }}
          />
        )}

        {/* Edit Appointment Modal */}
        {showEditAppointmentModal && selectedOpportunity && (
          <EditAppointmentModal
            opportunity={selectedOpportunity}
            onClose={() => {
              setShowEditAppointmentModal(false);
              setSelectedOpportunity(null);
            }}
            onSuccess={() => {
              setShowEditAppointmentModal(false);
              setSelectedOpportunity(null);
              fetchPipelineData();
              setSuccess('Appointment updated successfully!');
              setTimeout(() => setSuccess(null), 3000);
            }}
          />
        )}

        {/* Opportunity Edit Modal */}
        {showEditOpportunityModal && selectedOpportunity && (
          <OpportunityEditModal
            opportunity={selectedOpportunity}
            onClose={() => {
              setShowEditOpportunityModal(false);
              setSelectedOpportunity(null);
            }}
            onSuccess={() => {
              setShowEditOpportunityModal(false);
              setSelectedOpportunity(null);
              fetchPipelineData();
              setSuccess('Opportunity updated successfully!');
              setTimeout(() => setSuccess(null), 3000);
            }}
          />
        )}
      </div>
    </div>
  );
}

// Desktop Stage Box Component
const StageBox = ({ stage, opportunities, onStageChange, onCreateQuote, onScheduleAppointment, onEditAppointment, onEditOpportunity, stageTransitions, stageLabels }) => {
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 h-[500px] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{stage.icon}</span>
          <h3 className="font-semibold text-gray-800 text-sm">{stage.title}</h3>
        </div>
        <span className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full font-medium">
          {opportunities.length}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {opportunities.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <div className="text-4xl mb-2">📋</div>
            <p className="text-sm">No opportunities in this stage</p>
          </div>
        ) : (
          <div className="space-y-3">
            {opportunities.map((opportunity) => (
              <OpportunityCard
                key={opportunity.id}
                opportunity={opportunity}
                onStageChange={onStageChange}
                onCreateQuote={onCreateQuote}
                onScheduleAppointment={onScheduleAppointment}
                onEditAppointment={onEditAppointment}
                onEditOpportunity={onEditOpportunity}
                stageTransitions={stageTransitions}
                stageLabels={stageLabels}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Mobile Stage Section Component
const MobileStageSection = ({ stage, opportunities, isOpen, onToggle, onStageChange, onCreateQuote, onScheduleAppointment, onEditAppointment, onEditOpportunity, stageTransitions, stageLabels }) => {
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200">
      {/* Header - Clickable */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex justify-between items-center text-left hover:bg-gray-50 transition-colors duration-200"
      >
        <div className="flex items-center space-x-3">
          <span className="text-lg">{stage.icon}</span>
          <h3 className="font-semibold text-gray-800">{stage.title}</h3>
        </div>
        <div className="flex items-center space-x-3">
          <span className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full font-medium">
            {opportunities.length}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Content - Collapsible */}
      {isOpen && (
        <div className="border-t border-gray-200 p-4 max-h-[400px] overflow-y-auto">
          {opportunities.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <div className="text-3xl mb-2">📋</div>
              <p className="text-sm">No opportunities in this stage</p>
            </div>
          ) : (
            <div className="space-y-3">
              {opportunities.map((opportunity) => (
                <OpportunityCard
                  key={opportunity.id}
                  opportunity={opportunity}
                  onStageChange={onStageChange}
                  onCreateQuote={onCreateQuote}
                  onScheduleAppointment={onScheduleAppointment}
                  onEditAppointment={onEditAppointment}
                  onEditOpportunity={onEditOpportunity}
                  stageTransitions={stageTransitions}
                  stageLabels={stageLabels}
                  isMobile={true}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Opportunity Card Component
const OpportunityCard = ({ opportunity, onStageChange, onCreateQuote, onScheduleAppointment, onEditAppointment, onEditOpportunity, stageTransitions, stageLabels, isMobile = false }) => {
  const [showActionMenu, setShowActionMenu] = useState(false);
  const availableTransitions = stageTransitions[opportunity.status] || [];
  const isRepeatCustomer = opportunity.client_type === 'customer';


  return (
    <div className={`bg-gray-50 border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow duration-200 ${isMobile ? 'text-sm' : ''}`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          {/* Client Name + Project Name + Badges (conditionally rendered) */}
          {opportunity.status !== 'appointment_scheduled' && (
            <div className="flex items-center flex-wrap gap-2 mb-1">
              <h4 className={`font-semibold text-gray-900 ${isMobile ? 'text-sm' : 'text-sm'}`}>
                {/* Only show link if client_id is valid (not null, undefined, "null", or empty) */}
                {opportunity.client_id &&
                  opportunity.client_id !== 'null' &&
                  opportunity.client_id !== 'undefined' &&
                  String(opportunity.client_id).trim() !== '' ? (
                  <Link
                    to={`/clients/${opportunity.client_id}`}
                    className="hover:text-primary-600 hover:underline cursor-pointer"
                    title={`View client overview for ${opportunity.client_name}`}
                  >
                    {opportunity.client_name}
                  </Link>
                ) : (
                  <span className="text-gray-900 cursor-default" title="Client information not available - click appointment date to view details">
                    {opportunity.client_name}
                  </span>
                )}
              </h4>
              <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'} mb-1`}>
                {opportunity.project_name || 'Unnamed Project'}
              </p>
            </div>
          )}

          {/* Company */}
          {opportunity.company && (
            <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'} mb-1`}>
              {opportunity.company}
            </p>
          )}

          {/* Quote Link if exists */}
          {opportunity.quote_id && (
            <p className={`${isMobile ? 'text-xs' : 'text-xs'} mb-1 flex items-center justify-between`}>
              <span>
                📄 <Link
                  to={`/quotes/edit/${opportunity.quote_id}`}
                  className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                >
                  View Quote
                </Link>
              </span>
              {opportunity.estimated_value != null && opportunity.estimated_value > 0 && (
                <span className="font-medium text-primary-600">
                  {formatCurrency(opportunity.estimated_value)}
                </span>
              )}
            </p>
          )}

          {/* Show preferred date/time for appointment requests */}
          {opportunity.status === 'appointment_requested' && opportunity.preferred_date && (
            <button
              onClick={() => onScheduleAppointment(opportunity)}
              className={`text-blue-600 hover:text-blue-800 hover:underline font-medium ${isMobile ? 'text-xs' : 'text-xs'} mb-1 cursor-pointer text-left`}
            >
              📅 Preferred: {formatDate(opportunity.preferred_date)}
              {opportunity.preferred_time_range && ` at ${opportunity.preferred_time_range}`}
            </button>
          )}

          {/* Show actual appointment date/time for scheduled appointments */}
          {opportunity.status === 'appointment_scheduled' && (
            <div className="space-y-1 mb-1">
              <h4 className={`font-semibold text-gray-900 ${isMobile ? 'text-sm' : 'text-sm'}`}>
                {opportunity.client_id &&
                  opportunity.client_id !== 'null' &&
                  opportunity.client_id !== 'undefined' &&
                  String(opportunity.client_id).trim() !== '' ? (
                  <Link
                    to={`/clients/${opportunity.client_id}`}
                    className="hover:text-primary-600 hover:underline cursor-pointer"
                    title={`View client overview for ${opportunity.client_name}`}
                  >
                    {opportunity.client_name}
                  </Link>
                ) : (
                  <span className="text-gray-900 cursor-default">
                    {opportunity.client_name}
                  </span>
                )}
              </h4>
              <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                {opportunity.appointment_location ? (
                  <a
                    href={`https://maps.google.com/maps?q=${encodeURIComponent(opportunity.appointment_location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary-600 hover:underline cursor-pointer"
                    title="Open in maps"
                  >
                    📍 {opportunity.appointment_location}
                  </a>
                ) : (
                  <span className="text-gray-900 cursor-default">
                    📍 N/A
                  </span>
                )}
              </p>
              <button
                onClick={() => onEditAppointment(opportunity)}
                className={`text-green-600 hover:text-green-800 hover:underline font-medium ${isMobile ? 'text-xs' : 'text-xs'} cursor-pointer text-left`}
              >
                📅 {opportunity.appointment_date_time ? formatDate(opportunity.appointment_date_time.split('T')[0]) : 'N/A Date'}
                {opportunity.appointment_date_time ? ` at ${formatTime(opportunity.appointment_date_time.split('T')[1].substring(0, 5))}` : ' (N/A Time)'}
              </button>
            </div>
          )}

          {/* Show estimated value for quote-related stages */}
          {['quote_draft', 'quote_sent', 'quote_accepted', 'in_progress'].includes(opportunity.status) &&
            opportunity.estimated_value != null &&
            opportunity.estimated_value > 0 && (
              <p className={`text-primary-600 font-medium ${isMobile ? 'text-xs' : 'text-xs'} mb-1`}>
                {formatCurrency(opportunity.estimated_value)}
              </p>
            )}

          {/* Created date */}
          <p className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-xs'}`}>
            {formatDate(opportunity.created_at)}
          </p>
        </div>

        {/* Action Menu */}
        <div className="relative">
          <button
            onClick={() => setShowActionMenu(!showActionMenu)}
            className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>

          {showActionMenu && (
            <div className="absolute right-0 top-6 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[160px]">
              {/* Edit Opportunity */}
              <button
                onClick={() => {
                  onEditOpportunity(opportunity);
                  setShowActionMenu(false);
                }}
                className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                ✏️ Edit Details
              </button>

              {/* Stage-specific actions */}
              {opportunity.status === 'lead' && (
                <>
                  <button
                    onClick={() => {
                      onScheduleAppointment(opportunity);
                      setShowActionMenu(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50"
                  >
                    📅 Schedule Estimate
                  </button>
                  <button
                    onClick={() => {
                      onCreateQuote(opportunity);
                      setShowActionMenu(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-primary-600 hover:bg-primary-50"
                  >
                    📝 Create Quote
                  </button>
                </>
              )}

              {opportunity.status === 'appointment_requested' && (
                <>
                  <button
                    onClick={() => {
                      onScheduleAppointment(opportunity);
                      setShowActionMenu(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50"
                  >
                    📅 Schedule Appointment
                  </button>
                  <button
                    onClick={() => {
                      onCreateQuote(opportunity);
                      setShowActionMenu(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-primary-600 hover:bg-primary-50"
                  >
                    📝 Create Quote
                  </button>
                </>
              )}

              {opportunity.status === 'appointment_scheduled' && (
                <>
                  <button
                    onClick={() => {
                      onEditAppointment(opportunity);
                      setShowActionMenu(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50"
                  >
                    ✏️ Edit Appointment
                  </button>
                  <button
                    onClick={() => {
                      onCreateQuote(opportunity);
                      setShowActionMenu(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-primary-600 hover:bg-primary-50"
                  >
                    📝 Create Quote
                  </button>
                </>
              )}

              {/* Quote actions */}
              {['quote_draft', 'quote_sent'].includes(opportunity.status) && opportunity.quote_id && (
                <a
                  href={`/quotes/edit/${opportunity.quote_id}`}
                  className="block w-full text-left px-3 py-2 text-sm text-primary-600 hover:bg-primary-50"
                  onClick={() => setShowActionMenu(false)}
                >
                  ✏️ Edit Quote
                </a>
              )}

              {/* Stage transitions */}
              {availableTransitions.length > 0 && (
                <>
                  <div className="border-t border-gray-100 my-1"></div>
                  {availableTransitions.map((targetStatus) => (
                    <button
                      key={targetStatus}
                      onClick={() => {
                        onStageChange(opportunity.id, targetStatus);
                        setShowActionMenu(false);
                      }}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Move to {stageLabels[targetStatus]}
                    </button>
                  ))}
                </>
              )}

              <div className="border-t border-gray-100 my-1"></div>

              {/* Delete action */}
              <button
                onClick={async () => {
                  if (window.confirm('Are you sure you want to delete this opportunity?')) {
                    try {
                      await opportunitiesApi.deleteOpportunity(opportunity.id);
                      window.location.reload();
                    } catch (err) {
                      alert('Failed to delete opportunity: ' + err.message);
                    }
                  }
                  setShowActionMenu(false);
                }}
                className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                🗑️ Delete
              </button>

              <button
                onClick={() => setShowActionMenu(false)}
                className="block w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Close menu when clicking outside */}
      {showActionMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowActionMenu(false)}
        ></div>
      )}
    </div>
  );
};

// New Opportunity Modal Component
const NewOpportunityModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    // Client info
    clientName: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    // Opportunity info
    projectName: '',
    projectDescription: '',
    estimatedValue: '',
    notes: '',
    source: 'manual'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Use the capture endpoint to create both client and opportunity
      const response = await fetch('/api/opportunities/capture', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.clientName,
          company: formData.company,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          projectDescription: `${formData.projectName ? 'Project: ' + formData.projectName + '. ' : ''}${formData.projectDescription || ''}`,
          estimatedValue: formData.estimatedValue ? parseFloat(formData.estimatedValue) : null,
          source: formData.source
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create opportunity');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="New Opportunity">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorMessage message={error} />}

        {/* Client Information Section */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Client Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Client Name *</label>
              <input
                type="text"
                required
                value={formData.clientName}
                onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Company</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Project/Opportunity Information Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Project Information</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Project Name</label>
              <input
                type="text"
                value={formData.projectName}
                onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Kitchen Remodel, Bathroom Renovation"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Project Description</label>
              <textarea
                rows="3"
                value={formData.projectDescription}
                onChange={(e) => setFormData({ ...formData, projectDescription: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Estimated Value</label>
              <input
                type="number"
                step="0.01"
                value={formData.estimatedValue}
                onChange={(e) => setFormData({ ...formData, estimatedValue: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                rows="2"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Opportunity'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Schedule Appointment Modal Component
const ScheduleAppointmentModal = ({ opportunity, onClose, onSuccess, user }) => {
  const { user: authUser } = useAuth();
  const finalUser = user || authUser;

  const [formData, setFormData] = useState({
    title: `Estimate for ${opportunity.client_name} - ${opportunity.project_name || 'Project'}`,
    description: opportunity.service_details || opportunity.project_description || '',
    startDatetime: opportunity.preferred_date ?
      (opportunity.preferred_date.includes('T') ?
        `${opportunity.preferred_date.split('T')[0]}T09:00` :
        `${opportunity.preferred_date}T09:00`) : '',
    duration: 60, // Duration in minutes (default 1 hour)
    location: opportunity.client_address || '',
    status: 'scheduled'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newlyCreatedAppointment, setNewlyCreatedAppointment] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      // Import appointments API
      const { createAppointment } = await import('../api/appointments');

      // Calculate end time from start time + duration
      const startDate = new Date(formData.startDatetime);
      const endDate = new Date(startDate.getTime() + (formData.duration * 60000)); // Convert minutes to milliseconds

      // Create appointment with calculated end time
      const appointmentData = {
        title: formData.title,
        description: formData.description,
        startDatetime: formData.startDatetime,
        endDatetime: endDate.toISOString().slice(0, 16), // Format for datetime-local
        location: formData.location,
        status: formData.status,
        opportunityId: opportunity.id
      };

      const response = await createAppointment(appointmentData);
      setNewlyCreatedAppointment(response.appointment);

      // Update opportunity status to appointment_scheduled
      await opportunitiesApi.updateOpportunityStatus(opportunity.id, 'appointment_scheduled');

      // Open email modal instead of calling onSuccess directly
      setShowEmailModal(true);
    } catch (err) {
      setError(err.message || 'Failed to schedule appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Schedule Appointment">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorMessage message={error} />}

        {/* Display appointment request details */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-25 border border-blue-300 rounded-lg p-5 mb-6 shadow-sm">
          <h3 className="font-semibold text-blue-900 mb-4 text-base flex items-center">
            <span className="text-lg mr-2">👤</span>Client Information
          </h3>

          {/* Client Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Client Name */}
            <div className="bg-white rounded-md p-3 border border-blue-100">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Client Name</p>
              <p className="text-sm font-semibold text-gray-800 mt-1">{opportunity.client_name || 'Not provided'}</p>
            </div>

            {/* Phone Number */}
            <div className="bg-white rounded-md p-3 border border-blue-100">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Phone Number</p>
              <p className="text-sm font-semibold text-gray-800 mt-1">
                {opportunity.client_phone ? (
                  <a href={`tel:${opportunity.client_phone}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                    {opportunity.client_phone}
                  </a>
                ) : (
                  'Not provided'
                )}
              </p>
            </div>

            {/* Email Address */}
            <div className="bg-white rounded-md p-3 border border-blue-100">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Email Address</p>
              <p className="text-sm font-semibold text-gray-800 mt-1">
                {opportunity.client_email ? (
                  <a href={`mailto:${opportunity.client_email}`} className="text-blue-600 hover:text-blue-800 hover:underline break-all">
                    {opportunity.client_email}
                  </a>
                ) : (
                  'Not provided'
                )}
              </p>
            </div>

            {/* Address */}
            <div className="bg-white rounded-md p-3 border border-blue-100">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Address</p>
              <p className="text-sm font-semibold text-gray-800 mt-1">{opportunity.client_address || 'Not provided'}</p>
            </div>

            {/* Company */}
            {opportunity.company && (
              <div className="bg-white rounded-md p-3 border border-blue-100">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Company</p>
                <p className="text-sm font-semibold text-gray-800 mt-1">{opportunity.company}</p>
              </div>
            )}

            {/* Project Name */}
            <div className="bg-white rounded-md p-3 border border-blue-100">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Project/Service</p>
              <p className="text-sm font-semibold text-gray-800 mt-1">{opportunity.project_name || 'Not specified'}</p>
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-blue-200 my-4"></div>

          {/* Appointment Preferences */}
          <h4 className="font-semibold text-blue-900 mb-3 text-sm flex items-center">
            <span className="text-lg mr-2">📅</span>Preferred Appointment Details
          </h4>

          <div className="space-y-3">
            {/* Preferred Date */}
            {opportunity.preferred_date ? (
              <div className="flex items-start space-x-3 bg-blue-100 rounded-md p-3">
                <span className="text-lg">📅</span>
                <div>
                  <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Preferred Date</p>
                  <p className="text-base font-bold text-blue-900">{formatDate(opportunity.preferred_date)}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start space-x-3 bg-gray-100 rounded-md p-3 opacity-60">
                <span className="text-lg">📅</span>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Preferred Date</p>
                  <p className="text-sm text-gray-600">Not specified</p>
                </div>
              </div>
            )}

            {/* Preferred Time Range */}
            {opportunity.preferred_time_range ? (
              <div className="flex items-start space-x-3 bg-blue-100 rounded-md p-3">
                <span className="text-lg">⏰</span>
                <div>
                  <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Preferred Time</p>
                  <p className="text-base font-bold text-blue-900">{opportunity.preferred_time_range}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start space-x-3 bg-gray-100 rounded-md p-3 opacity-60">
                <span className="text-lg">⏰</span>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Preferred Time</p>
                  <p className="text-sm text-gray-600">Not specified</p>
                </div>
              </div>
            )}

            {/* Service Details */}
            {opportunity.service_details && (
              <div className="flex items-start space-x-3 bg-amber-50 rounded-md p-3 border border-amber-200">
                <span className="text-lg">📝</span>
                <div>
                  <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">Service Details</p>
                  <p className="text-sm text-amber-900 mt-1">{opportunity.service_details}</p>
                </div>
              </div>
            )}

            {/* Project Description */}
            {opportunity.project_description && !opportunity.service_details && (
              <div className="flex items-start space-x-3 bg-amber-50 rounded-md p-3 border border-amber-200">
                <span className="text-lg">📝</span>
                <div>
                  <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">Project Description</p>
                  <p className="text-sm text-amber-900 mt-1">{opportunity.project_description}</p>
                </div>
              </div>
            )}

            {/* Additional Notes */}
            {opportunity.notes && (
              <div className="flex items-start space-x-3 bg-purple-50 rounded-md p-3 border border-purple-200">
                <span className="text-lg">💬</span>
                <div>
                  <p className="text-xs font-medium text-purple-700 uppercase tracking-wide">Additional Notes</p>
                  <p className="text-sm text-purple-900 mt-1">{opportunity.notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Appointment Title *</label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date & Time *</label>
            <input
              type="datetime-local"
              required
              value={formData.startDatetime}
              onChange={(e) => setFormData({ ...formData, startDatetime: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Duration *</label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
              <option value={150}>2.5 hours</option>
              <option value={180}>3 hours</option>
              <option value={240}>4 hours</option>
            </select>
          </div>
        </div>

        {/* Show calculated end time */}
        {formData.startDatetime && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-sm text-gray-600">
              <strong>Appointment will end at:</strong> {' '}
              {new Date(new Date(formData.startDatetime).getTime() + (formData.duration * 60000)).toLocaleString()}
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Description/Notes</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows="3"
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? 'Scheduling...' : 'Schedule Appointment'}
          </button>
        </div>
      </form>

      {showEmailModal && newlyCreatedAppointment && opportunity.client_email && (
        <MessageModal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            onSuccess();
          }}
          templateType="appointment_scheduled"
          recipientEmail={opportunity.client_email || ''}
          contextData={{
            client_name: opportunity.client_name || '',
            appointment_title: newlyCreatedAppointment.title || '',
            appointment_date: moment(newlyCreatedAppointment.start_datetime).format('MMMM D, YYYY'),
            appointment_time: moment(newlyCreatedAppointment.start_datetime).format('h:mm A'),
            appointment_location: newlyCreatedAppointment.location || '',
            project_name: opportunity.project_name || '',
            company_name: finalUser?.companyName || finalUser?.username || ''
          }}
          opportunityId={newlyCreatedAppointment.opportunity_id || null}
          onSuccess={() => {
            setShowEmailModal(false);
            onSuccess();
          }}
        />
      )}
    </Modal>
  );
};

// Edit Appointment Modal Component
const EditAppointmentModal = ({ opportunity, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDatetime: '',
    duration: 60,
    location: '',
    status: 'scheduled'
  });
  const [loading, setLoading] = useState(false);
  const [loadingAppointment, setLoadingAppointment] = useState(true);
  const [error, setError] = useState(null);
  const [appointmentId, setAppointmentId] = useState(null);

  // Load existing appointment data when modal opens
  useEffect(() => {
    const loadAppointmentData = async () => {
      try {
        setLoadingAppointment(true);
        setError(null);

        // Fetch appointment by opportunity
        const { fetchAppointments } = await import('../api/appointments');
        const response = await fetchAppointments();
        const appointment = response.appointments?.find(apt => apt.opportunity_id === opportunity.id);

        if (appointment) {
          // Calculate duration from start and end times
          const startTime = new Date(appointment.start_datetime);
          const endTime = new Date(appointment.end_datetime);
          const duration = Math.round((endTime - startTime) / (1000 * 60)); // Convert to minutes

          setFormData({
            title: appointment.title || '',
            description: appointment.description || '',
            startDatetime: appointment.start_datetime ? appointment.start_datetime.slice(0, 16) : '', // Format for datetime-local input
            duration: duration || 60,
            location: appointment.location || '',
            status: appointment.status || 'scheduled'
          });
          setAppointmentId(appointment.id);
        } else {
          // No appointment found, set default values
          setFormData({
            title: `Estimate for ${opportunity.client_name} - ${opportunity.project_name || 'Project'}`,
            description: opportunity.service_details || opportunity.project_description || '',
            startDatetime: opportunity.preferred_date ? `${opportunity.preferred_date.split('T')[0]}T09:00` : '',
            duration: 60,
            location: opportunity.address || '',
            status: 'scheduled'
          });
        }
      } catch (err) {
        console.error('Error loading appointment:', err);
        setError(err.message || 'Failed to load appointment details');
        // Set default values on error
        setFormData({
          title: `Estimate for ${opportunity.client_name} - ${opportunity.project_name || 'Project'}`,
          description: opportunity.service_details || opportunity.project_description || '',
          startDatetime: opportunity.preferred_date ? `${opportunity.preferred_date.split('T')[0]}T09:00` : '',
          duration: 60,
          location: opportunity.address || '',
          status: 'scheduled'
        });
      } finally {
        setLoadingAppointment(false);
      }
    };

    loadAppointmentData();
  }, [opportunity]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      // Import appointments API
      const { updateAppointment } = await import('../api/appointments');

      // Calculate end time from start time + duration
      const startDate = new Date(formData.startDatetime);
      const endDate = new Date(startDate.getTime() + (formData.duration * 60000));

      const appointmentData = {
        title: formData.title,
        description: formData.description,
        startDatetime: formData.startDatetime,
        endDatetime: endDate.toISOString().slice(0, 16),
        location: formData.location,
        status: formData.status
      };

      if (appointmentId) {
        // Update existing appointment
        await updateAppointment(appointmentId, appointmentData);
      } else {
        // Create new appointment if none exists
        const { createAppointment } = await import('../api/appointments');
        appointmentData.opportunityId = opportunity.id;
        await createAppointment(appointmentData);

        // Update opportunity status
        await opportunitiesApi.updateOpportunityStatus(opportunity.id, 'appointment_scheduled');
      }

      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to update appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Edit Appointment">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorMessage message={error} />}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <h4 className="font-medium text-blue-900 mb-2">
            {appointmentId ? 'Editing Appointment:' : 'Creating New Appointment:'}
          </h4>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>Client:</strong> {opportunity.client_name}</p>
            <p><strong>Project:</strong> {opportunity.project_name || 'Project'}</p>
            {loadingAppointment ? (
              <p className="text-blue-600">Loading appointment details...</p>
            ) : appointmentId ? (
              <div className="space-y-1 mt-2">
                <p><strong>Current Status:</strong> <span className="capitalize">{formData.status}</span></p>
                {formData.startDatetime && (
                  <p><strong>Scheduled:</strong> {new Date(formData.startDatetime).toLocaleString()}</p>
                )}
                {formData.location && (
                  <p><strong>Location:</strong> {formData.location}</p>
                )}
              </div>
            ) : (
              <p className="text-blue-600">No existing appointment found. Creating new appointment.</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Appointment Title *</label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date & Time *</label>
            <input
              type="datetime-local"
              required
              value={formData.startDatetime}
              onChange={(e) => setFormData({ ...formData, startDatetime: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Duration *</label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
              <option value={150}>2.5 hours</option>
              <option value={180}>3 hours</option>
              <option value={240}>4 hours</option>
            </select>
          </div>
        </div>

        {/* Show calculated end time */}
        {formData.startDatetime && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-sm text-gray-600">
              <strong>Appointment will end at:</strong> {' '}
              {new Date(new Date(formData.startDatetime).getTime() + (formData.duration * 60000)).toLocaleString()}
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="rescheduled">Rescheduled</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Description/Notes</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows="3"
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || loadingAppointment}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loadingAppointment ? 'Loading...' : loading ? (appointmentId ? 'Updating...' : 'Creating...') : (appointmentId ? 'Update Appointment' : 'Create Appointment')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Opportunity Edit Modal Component
const OpportunityEditModal = ({ opportunity, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    projectName: opportunity.project_name || '',
    projectDescription: opportunity.project_description || '',
    estimatedValue: opportunity.estimated_value || '',
    notes: opportunity.notes || '',
    disableAutoEmails: !!opportunity.disable_auto_emails
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      const updateData = {
        projectName: formData.projectName.trim() || null,
        projectDescription: formData.projectDescription.trim() || null,
        estimatedValue: formData.estimatedValue ? parseFloat(formData.estimatedValue) : null,
        notes: formData.notes.trim() || null,
        disableAutoEmails: formData.disableAutoEmails
      };

      await opportunitiesApi.updateOpportunity(opportunity.id, updateData);
      onSuccess();
    } catch (err) {
      console.error('Error updating opportunity:', err);
      setError(err.message || 'Failed to update opportunity');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Edit Opportunity">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            <strong>Client:</strong> {opportunity.client_name}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project Name
          </label>
          <input
            type="text"
            value={formData.projectName}
            onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g., Kitchen Remodel, Bathroom Renovation"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project Description
          </label>
          <textarea
            value={formData.projectDescription}
            onChange={(e) => setFormData({ ...formData, projectDescription: e.target.value })}
            rows={3}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Estimated Value
          </label>
          <input
            type="number"
            step="0.01"
            value={formData.estimatedValue}
            onChange={(e) => setFormData({ ...formData, estimatedValue: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="0.00"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex items-center space-x-2 pt-2">
          <input
            type="checkbox"
            id="disableAutoEmails"
            checked={formData.disableAutoEmails}
            onChange={(e) => setFormData({ ...formData, disableAutoEmails: e.target.checked })}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="disableAutoEmails" className="text-sm font-medium text-gray-700">
            Pause Automated Emails
          </label>
        </div>
        <p className="text-xs text-gray-500 ml-6">
          If checked, this opportunity will not receive any automated system emails (e.g. follow-ups). Manual emails can still be sent.
        </p>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Update Opportunity'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// New Appointment Modal Component (without opportunity)
const NewAppointmentModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    clientId: '',
    title: '',
    description: '',
    startDatetime: '',
    duration: 60,
    location: '',
    status: 'scheduled'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const { fetchClients: fetchClientsApi } = await import('../api/clients');
        const response = await fetchClientsApi();
        setClients(response.clients || []);
      } catch (err) {
        console.error('Failed to fetch clients:', err);
      }
    };
    fetchClients();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.clientId || !formData.title || !formData.startDatetime) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { createAppointment } = await import('../api/appointments');

      const startDate = new Date(formData.startDatetime);
      const endDate = new Date(startDate.getTime() + (formData.duration * 60000));

      const appointmentData = {
        clientId: parseInt(formData.clientId),
        title: formData.title,
        description: formData.description,
        startDatetime: formData.startDatetime,
        endDatetime: endDate.toISOString().slice(0, 16),
        location: formData.location,
        status: formData.status
      };

      await createAppointment(appointmentData);
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to schedule appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Schedule New Appointment">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorMessage message={error} />}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Client <span className="text-red-500">*</span>
          </label>
          <select
            required
            value={formData.clientId}
            onChange={(e) => {
              const clientId = e.target.value;
              const selectedClient = clients.find(c => c.id === parseInt(clientId));
              setFormData({
                ...formData,
                clientId: clientId,
                location: selectedClient?.address || formData.location
              });
            }}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select a client</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g., Initial Consultation"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Appointment details..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date & Time <span className="text-red-500">*</span>
          </label>
          <input
            type="datetime-local"
            required
            value={formData.startDatetime}
            onChange={(e) => setFormData({ ...formData, startDatetime: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Duration (minutes)
          </label>
          <input
            type="number"
            min="15"
            step="15"
            value={formData.duration}
            onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location
          </label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g., Conference Room A or Zoom"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? 'Scheduling...' : 'Schedule Appointment'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SalesPipelinePage;